import { useMemo } from 'react'
import { Link, createFileRoute } from '@tanstack/react-router'
import { queryOptions, useQueries, useQuery } from '@tanstack/react-query'
import type { Brain, CostEstimate } from '@algoboo/schema/api'
import { Card, EmptyState, Icon, Pill, Skeleton, buttonClasses, cx, formatCount } from '@algoboo/ui'
import { brainsQuery, qk, request, useBrains } from '../lib/api'
import { prefetch } from '../lib/route-data'
import { usePageMeta } from '../lib/meta'
import { brainUsage, orderBrains, usdPerMillion } from '../lib/stats'
import { PageHeader } from '../components/Shell'
import { QueryError } from '../components/widgets'

/** The estimate's reference agent: one tick every 10 minutes in plan mode (planner + risk critic per tick). */
const TICKS_PER_DAY = 144
const MODE = 'plan' as const
const SAMPLE = 200

const estimateQuery = (id: string) =>
  queryOptions({
    queryKey: qk.brainEstimate(id, TICKS_PER_DAY, MODE),
    queryFn: ({ signal }) => request('getBrainEstimate', { signal, params: { id }, query: { ticksPerDay: TICKS_PER_DAY, mode: MODE } }),
    staleTime: 10 * 60_000,
  })

export const Route = createFileRoute('/brains')({
  // the catalog, then every enabled brain's estimate
  loader: ({ context: { queryClient: qc } }) =>
    prefetch(qc.ensureQueryData(brainsQuery()).then((list) => prefetch(...list.filter((b) => b.enabled).map((b) => qc.ensureQueryData(estimateQuery(b.id)))))),
  component: BrainsPage,
})

const TIERS: { tier: Brain['tier']; title: string; body: string }[] = [
  { tier: 'frontier', title: 'Frontier', body: 'The strongest reasoning for planners that weigh many signals. Higher cost per tick.' },
  { tier: 'cheap', title: 'Fast and cheap', body: 'Quick, low-cost models for rules-first agents and frequent ticks. The default for new agents.' },
]

const PROVIDER: Record<string, string> = { anthropic: 'Anthropic', openai: 'OpenAI', xai: 'xAI', google: 'Google', other: 'Other' }
/** OpenRouter vendor slugs as their companies write them. */
const VENDOR: Record<string, string> = { openai: 'OpenAI', 'x-ai': 'xAI', google: 'Google', anthropic: 'Anthropic', deepseek: 'DeepSeek', 'z-ai': 'Z.ai', 'meta-llama': 'Meta', mistralai: 'Mistral', qwen: 'Qwen', moonshotai: 'Moonshot AI' }

/** `openrouter/vendor/model` → vendor, else the catalog provider. */
function vendorOf(b: Brain): string {
  const parts = b.id.split('/')
  if (parts[0] === 'openrouter' && parts[1]) return VENDOR[parts[1]] ?? parts[1].replace(/(^|-)([a-z])/g, (_m, d: string, c: string) => `${d ? ' ' : ''}${c.toUpperCase()}`)
  return PROVIDER[b.provider] ?? b.provider
}

function BrainsPage() {
  usePageMeta('Brains', 'The language models algo.boo agents can think with, their tiers and what they cost per million tokens and per month.')
  const brains = useBrains()
  const ordered = useMemo(() => orderBrains(brains.data ?? []), [brains.data])
  const estimates = useQueries({
    queries: ordered.map((b) => ({ ...estimateQuery(b.id), enabled: b.enabled })),
  })
  const byId = Object.fromEntries(ordered.map((b, i) => [b.id, estimates[i]]))
  // Counts come from GET /brains (`agentsCount`); an older API without it falls back to the newest public agents.
  const counted = !!brains.data && brains.data.every((b) => b.agentsCount !== undefined)
  const agents = useQuery({ queryKey: ['brains', 'agents-sample'], queryFn: ({ signal }) => request('listAgents', { signal, query: { range: 'all', sort: 'new', limit: SAMPLE } }), staleTime: 60_000, enabled: !!brains.data && !counted })
  const usage = useMemo(() => brainUsage(agents.data?.items ?? []), [agents.data])
  const partial = !counted && !!agents.data?.nextCursor
  const countOf = (b: Brain): number | null => (counted ? (b.agentsCount ?? 0) : agents.data ? (usage.get(b.id) ?? 0) : null)

  return (
    <>
      <PageHeader
        eyebrow="Catalog"
        title="Brains"
        lede="The models an agent can think with. The brain plans and explains; the strategy, the permissions you sign and the policy checks decide what it may trade. Prices are the current rate card, markup included."
        actions={
          <Link to="/create" className={buttonClasses('primary', 'md')}>
            <Icon name="plus" size={14} className="-ml-0.5" /> Make an agent
          </Link>
        }
      />
      {brains.error ? (
        <QueryError error={brains.error} refetch={() => void brains.refetch()} compact={false} />
      ) : brains.isLoading ? (
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-24 rounded-xl" />
          ))}
        </div>
      ) : ordered.length === 0 ? (
        <EmptyState title="No brains in the catalog" body="The operator has not enabled any model yet. Agents without a brain still run their rules." />
      ) : (
        <div className="space-y-8">
          {TIERS.map((t) => {
            const list = ordered.filter((b) => b.tier === t.tier)
            if (!list.length) return null
            return (
              <section key={t.tier} aria-labelledby={`tier-${t.tier}`} data-testid={`tier-${t.tier}`}>
                <div className="mb-3">
                  <h2 id={`tier-${t.tier}`} className="font-display text-[18px] font-semibold">
                    {t.title}
                  </h2>
                  <p className="text-[13px] text-ink-3">{t.body}</p>
                </div>
                <Card className="overflow-hidden">
                  <ul className="divide-y divide-line">
                    {list.map((b) => {
                      const est = byId[b.id]
                      return <BrainRow key={b.id} brain={b} estimate={est?.data} estimateLoading={!!est?.isLoading} estimateFailed={!!est?.error} agents={countOf(b)} partial={partial} />
                    })}
                  </ul>
                </Card>
              </section>
            )
          })}
          <p className="text-[12px] text-ink-3">
            Monthly figures assume one tick every 10 minutes in plan mode (a planner call and a risk-critic call per tick, typical token counts), 30 days. Rules-only agents call no model and cost nothing here. Every agent has a daily spend cap its owner sets.
            {(counted || agents.data) && ` Agent counts cover ${partial ? `the newest ${SAMPLE}` : 'all'} public agents.`}
          </p>
        </div>
      )}
    </>
  )
}

function BrainRow({ brain: b, estimate, estimateLoading, estimateFailed, agents, partial }: { brain: Brain; estimate: CostEstimate | undefined; estimateLoading: boolean; estimateFailed: boolean; agents: number | null; partial: boolean }) {
  return (
    <li className={cx('grid grid-cols-1 gap-3 px-4 py-4 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center sm:px-5', !b.enabled && 'opacity-60')} data-testid="brain-row" data-brain={b.id}>
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[15px] font-semibold text-ink">{b.displayName}</span>
          <span className="text-[12.5px] text-ink-3">{vendorOf(b)}</span>
          {b.enabled ? (
            <Pill tone="good">Available</Pill>
          ) : (
            <Pill tone="neutral" title="Not offered for new agents right now">
              Unavailable
            </Pill>
          )}
        </div>
        <div className="mt-1 truncate font-mono text-[11.5px] text-ink-3">{b.id}</div>
        {agents !== null && (
          <div className="mt-1 text-[12px] text-ink-3" data-testid="brain-agents">
            {agents === 0 ? (
              'No agents yet'
            ) : (
              <Link to="/agents" search={{ brain: b.id }} className="text-accent hover:underline">
                {`${formatCount(agents)}${partial ? '+' : ''} ${agents === 1 ? 'agent' : 'agents'}`}
              </Link>
            )}
          </div>
        )}
      </div>
      <dl className="grid grid-cols-3 gap-4 text-right sm:min-w-[340px]">
        <Price label="Input" hint="per 1M tokens" value={estimate ? `$${usdPerMillion(estimate.inputMicroPer1k, estimate.markupBps)}` : null} loading={estimateLoading} failed={estimateFailed || !b.enabled} />
        <Price label="Output" hint="per 1M tokens" value={estimate ? `$${usdPerMillion(estimate.outputMicroPer1k, estimate.markupBps)}` : null} loading={estimateLoading} failed={estimateFailed || !b.enabled} />
        <Price label="Per month" hint="plan mode, 10 min" value={estimate ? `$${Number(estimate.usdPerMonth).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : null} loading={estimateLoading} failed={estimateFailed || !b.enabled} strong />
      </dl>
    </li>
  )
}

function Price({ label, hint, value, loading, failed, strong }: { label: string; hint: string; value: string | null; loading: boolean; failed: boolean; strong?: boolean }) {
  return (
    <div className="min-w-0">
      <dt className="text-[10.5px] font-medium uppercase tracking-[0.06em] text-ink-3">{label}</dt>
      <dd className={cx('num mt-0.5 font-mono text-[13.5px]', strong ? 'text-ink' : 'text-ink-2')}>{value ?? (loading && !failed ? <Skeleton className="ml-auto h-4 w-12" /> : '—')}</dd>
      <dd className="text-[10.5px] text-ink-3">{hint}</dd>
    </div>
  )
}
